import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const KIND_LABEL = { booker: "Booker", salesman: "Salesman", developer: "Developer" };
export const TYPE_LABEL = { work: "Earned", plus: "Bonus", payment: "Paid", minus: "Deduction" };
const COMPANY = { indane: "Indane", bharat: "Bharat", hp: "HP" };
const num = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const rangeLabel = (from, to) => (from ? (from === to ? from : `${from} to ${to}`) : `All time up to ${to}`);
const safe = (s) => String(s || "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "").toUpperCase();

export const rateText = (p) => {
  const r = p.current_rate;
  if (p.kind === "booker" || !r) return "-";
  return p.kind === "salesman" ? `${r.rate}` : `I ${r.indane} · B ${r.bharat} · H ${r.hp}`;
};

export const obDir = (amt) => (amt < 0 ? "Receivable" : "Payable");
const STMT_HEAD = ["Date", "Type", "Qty", "Rate", "Earned", "Bonus", "Paid", "Deduction", "Note", "Balance"];
const STMT_W = [12, 12, 8, 24, 12, 12, 12, 12, 30, 14];
const openingLine = (st, from) => {
  const ob = st.opening_balance;
  return st.opening_kind === "opening"
    ? [ob.date, "Opening", "", "", "", "", "", "", [obDir(ob.amount), ob.note].filter(Boolean).join(" · "), st.opening]
    : st.opening_kind === "carry"
      ? [from || "", "Brought forward", "", "", "", "", "", "", "Balance before this period", st.opening]
      : ["", "Opening", "", "", "", "", "", "", "Not set", st.opening];
};
const priorList = (st) => (st.opening_kind === "carry" ? st.prior || [] : []);
const priorRows = (st) =>
  priorList(st).map((x) => {
    if (x.type === "opening") return [x.date, "- Opening", "", "", "", "", "", "", [`${obDir(x.amount)} Rs. ${num(Math.abs(x.amount))}`, x.note].filter(Boolean).join(" · "), ""];
    if (x.type === "work") return [x.date === x.to ? x.date : `${x.date} to ${x.to}`, "- Earned", x.qty, "", x.amount, "", "", "", "Bookings before this period", ""];
    const c = (t) => (x.type === t ? x.amount : "");
    return [x.date, `- ${TYPE_LABEL[x.type]}`, "", "", "", c("plus"), c("payment"), c("minus"), x.note || "", ""];
  });
const stmtBody = (st, from) => [
  openingLine(st, from),
  ...priorRows(st),
  ...st.rows.map((r) => {
    const w = r.type === "work";
    return [r.date, TYPE_LABEL[r.type], w ? r.qty : "", w ? r.rate_label : "", w ? r.amount : "", r.type === "plus" ? r.amount : "",
      r.type === "payment" ? r.amount : "", r.type === "minus" ? r.amount : "", [r.mode, r.note].filter(Boolean).join(" · "), r.balance];
  }),
];
const SUM_HEAD = ["Name", "Rate", "Agencies", "Qty", "Opening", "Earned", "Bonus", "Paid", "Deduction", "Balance"];
const sumBody = (people) =>
  people.map((p) => [p.name, rateText(p), p.agency_names?.length ? `${p.agency_names.length} agencies` : "-",
    p.qty, p.opening, p.earned, p.plus, p.paid, p.minus, p.balance]);
const stLine = (s) =>
  `${s.opening_kind === "carry" ? "Brought forward" : "Opening"} Rs. ${num(s.opening)}  |  Qty ${s.qty}  |  Earned Rs. ${num(s.earned)}  |  Bonus Rs. ${num(s.plus)}  |  Paid Rs. ${num(s.paid)}  |  Deductions Rs. ${num(s.minus)}  |  Balance Rs. ${num(s.balance)}`;

const agencyTable = (st, kind) => {
  const rows = st.agency_rows || [];
  const qty = rows.reduce((a, r) => a + r.qty, 0);
  const amt = rows.reduce((a, r) => a + r.amount, 0);
  return kind === "developer"
    ? { head: ["Agency", "Company", "Fresh Qty", "Rate", "Earned"], widths: [44, 12, 12, 10, 14],
        body: rows.map((r) => [r.agency, COMPANY[r.company] || "", r.qty, r.rate_label, r.amount]), total: ["TOTAL", "", qty, "", amt] }
    : { head: ["Agency", "Fresh Qty", "Earned"], widths: [44, 12, 14], body: rows.map((r) => [r.agency, r.qty, r.amount]), total: ["TOTAL", qty, amt] };
};

async function saveWb(wb, filename) {
  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const sheetName = (wb, name) => {
  const base = String(name).replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 28) || "Sheet";
  let n = base;
  for (let i = 2; wb.getWorksheet(n); i += 1) n = `${base} ${i}`;
  return n;
};

function addSheet(wb, name, { title, lines = [], head, body, widths, total }) {
  const ws = wb.addWorksheet(sheetName(wb, name));
  ws.addRow([title]).font = { bold: true, size: 14 };
  lines.forEach((l) => ws.addRow([l]));
  ws.addRow([]);
  const h = ws.addRow(head);
  h.font = { bold: true, color: { argb: "FFFFFFFF" } };
  h.eachCell((c) => (c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF385C4B" } }));
  body.forEach((r) => ws.addRow(r));
  if (total) ws.addRow(total).font = { bold: true };
  ws.columns.forEach((c, i) => (c.width = widths[i] || 12));
}

const pdfFmt = (v) => (typeof v === "number" ? (Number.isInteger(v) ? String(v) : num(v)) : v);
function pdfSection(doc, { title, lines = [], head, body, total, muted = 0 }, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(y === 40 ? 15 : 12);
  doc.text(title, 32, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  let yy = y + 18;
  lines.forEach((l) => {
    const parts = doc.splitTextToSize(l, 770);
    doc.text(parts, 32, yy);
    yy += 14 * parts.length;
  });
  autoTable(doc, {
    startY: yy + 6, head: [head], body: body.map((r) => r.map(pdfFmt)), styles: { fontSize: 8 }, headStyles: { fillColor: [56, 92, 75] },
    didParseCell: (d) => {
      if (d.section === "body" && d.row.index >= 1 && d.row.index <= muted) Object.assign(d.cell.styles, { textColor: 110, fontStyle: "italic" });
    },
    ...(total ? { foot: [total.map(pdfFmt)], footStyles: { fillColor: [240, 240, 234], textColor: 20, fontStyle: "bold" }, showFoot: "lastPage" } : {}),
  });
  return doc.lastAutoTable.finalY;
}
const newPdf = () => new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
const nextY = (doc, y) => {
  if (y + 90 < doc.internal.pageSize.getHeight()) return y + 30;
  doc.addPage();
  return 40;
};

const stmtSection = (st, from, to, title) => {
  const p = st.person || st;
  const lines = [`Period: ${rangeLabel(from, to)}`];
  if (p.kind !== "booker") lines.push(`Rate: ${rateText(p)}`);
  const ob = st.opening_balance;
  if (ob) lines.push(`Opening balance: Rs. ${num(Math.abs(ob.amount))} (${obDir(ob.amount)}) · from ${ob.date} — earlier entries are not counted`);
  lines.push(stLine(st));
  return { title, lines, head: STMT_HEAD, body: stmtBody(st, from), widths: STMT_W, muted: priorList(st).length };
};
const agencySection = (st, p) => ({ title: `${p.name} — Agency-wise (fresh bookings)`, ...agencyTable(st, p.kind) });
const stmtFile = (p, to) => `${safe(p.name)}_${p.kind.toUpperCase()}_${to}`;

export async function statementExcel(st, from, to) {
  const p = st.person;
  const wb = new ExcelJS.Workbook();
  addSheet(wb, "Statement", stmtSection(st, from, to, `${KIND_LABEL[p.kind]}: ${p.name}`));
  if (st.agency_rows) addSheet(wb, "Agency-wise", agencySection(st, p));
  await saveWb(wb, `${stmtFile(p, to)}.xlsx`);
}

export function statementPdf(st, from, to) {
  const p = st.person;
  const doc = newPdf();
  const y = pdfSection(doc, stmtSection(st, from, to, `${KIND_LABEL[p.kind]}: ${p.name}`), 40);
  if (st.agency_rows) pdfSection(doc, agencySection(st, p), nextY(doc, y));
  doc.save(`${stmtFile(p, to)}.pdf`);
}

const summarySection = (kind, data, from, to) => ({
  title: `${KIND_LABEL[kind]} Summary`, lines: [`Period: ${rangeLabel(from, to)}`, stLine(data.totals)],
  head: SUM_HEAD, body: sumBody(data.people), widths: [26, 18, 14, 8, 12, 12, 12, 12, 12, 14],
});

export async function summaryExcel(kind, data, from, to) {
  const wb = new ExcelJS.Workbook();
  addSheet(wb, "Summary", summarySection(kind, data, from, to));
  data.people.forEach((p) => addSheet(wb, p.name, stmtSection(p, from, to, `${p.name} — Date-wise`)));
  await saveWb(wb, `${KIND_LABEL[kind].toUpperCase()}_SUMMARY_${to}.xlsx`);
}

export function summaryPdf(kind, data, from, to) {
  const doc = newPdf();
  pdfSection(doc, summarySection(kind, data, from, to), 40);
  data.people.forEach((p) => {
    doc.addPage();
    pdfSection(doc, stmtSection(p, from, to, `${p.name} — Date-wise`), 40);
  });
  doc.save(`${KIND_LABEL[kind].toUpperCase()}_SUMMARY_${to}.pdf`);
}
